import { useRef, useEffect } from 'react';
import {
    select, scaleUtc, extent, scaleLinear, max,
    area, axisBottom, axisLeft, timeFormat, curveMonotoneX, scaleOrdinal,
    line
} from 'd3';
import moment from 'moment';

import useResizeObserver from 'Utils/resizeObserver';
import { tooltip } from './Component'; // Assuming this is correctly defined
import './Charts.css';

const Areachart = ({ wrapperRef, data }) => {
    const svgRef = useRef();
    const dimensions = useResizeObserver(wrapperRef);

    useEffect(() => {
        if (!data || !dimensions) return;

        const svg = select(svgRef.current);
        const { width, height } = dimensions; // Destructure for easier use
        const marginTop = 20;
        const marginRight = 20;
        const marginBottom = 20;
        const marginLeft = 30;

        const xScale = scaleUtc(extent(data, d => new Date(d.date)), [marginLeft, width - marginRight]);

        const values = Object.keys(data[0]).filter((key) => key !== 'date');

        const yScale = scaleLinear([0, max(data, (d) => max(values.map((key) => d[key]))) + 10], [height - marginBottom, marginTop]);

        const formatDate = timeFormat("%b %d");

        svg.select(".x-axis")
            .attr("transform", `translate(0,${height - marginBottom})`)
            .call(axisBottom(xScale).ticks(width / 80).tickFormat(formatDate).tickSizeOuter(0));

        svg.select(".y-axis")
            .attr("transform", `translate(${marginLeft},0)`)
            .call(axisLeft(yScale));

        const colorScale = scaleOrdinal()
            .domain(values)
            .range(['#2884FF', '#FF6370', '#00C48C', '#FFD700', '#FF8C00']);

        // Use the original 'data' array for the area and line paths
        values.forEach((key, index) => {
            svg.selectAll(`.area-${key}`) // Use a more specific class
                .data([data]) // Bind the data array ONCE for each area
                .join("path")
                .attr("class", `area area-${key}`)
                .attr("fill", colorScale(key))
                .attr("fill-opacity", 0.3)
                .attr("stroke", "none")
                .attr("d", area()
                    .x((d) => xScale(new Date(d.date)))
                    .y0(height - marginBottom)
                    .y1((d) => yScale(d[key]))
                    .curve(curveMonotoneX))

            svg.selectAll(`.top-stroke-${key}`)
                .data([data]) // Bind the data array ONCE for each line
                .join("path")
                .attr("class", `top-stroke top-stroke-${key}`)
                .attr("fill", "none")
                .attr("stroke", colorScale(key))
                .attr("stroke-width", 2)
                .attr("d", line()
                    .x((d) => xScale(new Date(d.date)))
                    .y((d) => yScale(d[key]))
                    .curve(curveMonotoneX));


            // Dots (Improved with bisector)
            svg.selectAll(`.dot-${key}`)
                .data(data)
                .join("circle")
                .attr("class", `area-dot dot-${key}`)
                .attr("cx", (d) => xScale(new Date(d.date)))
                .attr("cy", (d) => yScale(d[key]))
                .attr("r", 2)
                .attr("fill", colorScale(key))
                .attr("stroke-width", 1.5)
                .on('mouseover', function (event, d) {
                    const tooltipContent = `
                        <strong>Date:</strong> ${moment(new Date(d.date)).format("DD MMM YYYY")}
                        ${values.map((k) => `<br/><strong>${k}:</strong> ${d[k]}`).join('')}
                    `;

                    tooltip.transition().style('opacity', 0.9);
                    tooltip.html(tooltipContent)
                        .style('left', `${event.pageX}px`)
                        .style('top', `${event.pageY - 28}px`);
                })
                .on('mouseout', () => {
                    tooltip.transition().duration(500).style('opacity', 0);
                });
        });


    }, [data, dimensions]);

    return (
        <div className='chart_wrapper_container'>
            <svg ref={svgRef}>
                <g className='x-axis' />
                <g className='y-axis area-y-axis' />
            </svg>
        </div>
    );
}

export default Areachart;